import { supabase, getValidAccessToken } from '../../../lib/supabase';
import type {
    PlanId,
    SubscriptionPlan,
    OrganizationSubscription,
    CreateCheckoutDTO,
    CheckoutSessionResponse
} from '../../../domain/entities/Subscription';
import { 
    mapPlanRowToEntity, 
    mapSubscriptionRowToEntity, 
    // type OrganizationSubscriptionRow, -- Removed legacy types
    // type SubscriptionPlanRow -- Removed legacy types
} from './subscription.mapper';
import { Database } from '../../../src/types/database.types';

type SubscriptionPlanRow = Database['public']['Tables']['subscription_plans']['Row'];
type OrganizationSubscriptionRow = Database['public']['Tables']['organizations']['Row'];

// =============================================================================
// API METHODS
// =============================================================================

/**
 * Busca todos os planos disponíveis
 */
export async function fetchAvailablePlans(): Promise<SubscriptionPlan[]> {
    const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_per_user', { ascending: true });

    if (error) {
        console.error('[subscriptionService] Error fetching plans:', error);
        throw new Error('Falha ao carregar planos');
    }

    // Data is already typed via Database definition if we injected it into supabase client, 
    // but here we are using a generic client, so we cast to the generated Row type safely.
    return (data as SubscriptionPlanRow[] || []).map(mapPlanRowToEntity);
}

/**
 * Busca um plano específico por ID
 */
export async function fetchPlanById(planId: PlanId): Promise<SubscriptionPlan | null> {
    const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', planId)
        .single();

    if (error) {
        console.error('[subscriptionService] Error fetching plan:', error);
        return null;
    }

    return data ? mapPlanRowToEntity(data as SubscriptionPlanRow) : null;
}

/**
 * Busca o estado atual da assinatura de uma organização
 */
export async function fetchOrganizationSubscription(
    organizationId: string
): Promise<OrganizationSubscription | null> {
    const { data, error } = await supabase
        .from('organizations')
        .select('plan, subscription_status, stripe_customer_id, stripe_subscription_id, quantity, trial_ends_at, blocked_at')
        .eq('id', organizationId)
        .single();

    if (error) {
        console.error('[subscriptionService] Error fetching subscription:', error);
        return null;
    }

    return data ? mapSubscriptionRowToEntity(data as OrganizationSubscriptionRow) : null;
}

/**
 * Cria sessão de checkout para upgrade de plano
 */
export async function createCheckoutSession(
    dto: CreateCheckoutDTO
): Promise<CheckoutSessionResponse> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
        throw new Error('Usuário não autenticado');
    }

    const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(dto),
        }
    );

    if (!response.ok) {
        let errorMessage = 'Falha ao criar sessão de checkout';
        try {
            const errorJson = await response.json();
            errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch (e) {
            const text = await response.text();
            errorMessage = text || `Erro ${response.status}: ${response.statusText}`;
            console.error('[createCheckoutSession] Erro não-JSON:', text);
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export interface SyncOrgBillingResponse {
    synced: boolean;
    organizationId: string;
    stripeSubscriptionId?: string;
    plan?: string;
    subscriptionStatus?: string;
    reason?: string;
}

/**
 * Sincroniza assinatura da organização com o Stripe (fallback quando webhook não atualiza o banco).
 */
export async function syncOrganizationBilling(
    organizationId?: string,
    sessionId?: string
): Promise<SyncOrgBillingResponse> {
    if (!organizationId && !sessionId) {
        throw new Error('organizationId ou sessionId é obrigatório');
    }

    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
        throw new Error('Usuário não autenticado');
    }

    const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-org-billing`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
                ...(organizationId ? { organizationId } : {}),
                ...(sessionId ? { sessionId } : {}),
            }),
        }
    );

    if (!response.ok) {
        let errorMessage = 'Falha ao sincronizar assinatura';
        try {
            const errorJson = await response.json();
            errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
            errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

/**
 * Redireciona para o portal do cliente Stripe (gerenciar assinatura)
 */
export async function createCustomerPortalSession(
    organizationId: string,
    returnUrl: string
): Promise<{ url: string }> {
    const accessToken = await getValidAccessToken();

    const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-portal`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
                apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ organizationId, returnUrl }),
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Falha ao acessar portal do cliente');
    }

    return response.json();
}

// =============================================================================
// UPDATE SUBSCRIPTION QUANTITY
// =============================================================================

export interface UpdateQuantityResponse {
    success: boolean;
    type?: 'upgrade' | 'downgrade';
    message: string;
    previousQuantity?: number;
    newQuantity?: number;
    effectiveDate?: string;
}

/**
 * Atualiza a quantidade de licenças da assinatura
 * - Upgrades: cobrança imediata com rateio
 * - Downgrades: agendado para fim do período
 */
export async function updateSubscriptionQuantity(
    organizationId: string,
    newQuantity: number,
    planId?: PlanId
): Promise<UpdateQuantityResponse> {
    // Garantir sessão válida antes de invocar a Edge Function (evita 401)
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Usuário não autenticado');

    const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-subscription`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ organizationId, newQuantity, ...(planId ? { planId } : {}) }),
        }
    );

    if (!response.ok) {
        let errorMessage = 'Falha ao atualizar condições de licenças';
        try {
            const errorJson = await response.json();
            errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
            errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }
        console.error('[updateSubscriptionQuantity] Error:', errorMessage);
        throw new Error(errorMessage);
    }

    const data = await response.json();

    // A função pode retornar success:false com tipo especial (ex: no_subscription para trials)
    if (data && !data.success) {
        if (data.type === 'no_subscription') {
            throw new Error('Nenhuma assinatura ativa. Assine um plano para gerenciar licenças.');
        }
        throw new Error(data.message || 'Falha ao atualizar licenças');
    }

    return data;
}
